import type { AccessibilitySettings, FontSize } from '@/lib/a11y/types'
import type { NeedsCategory, NeedsProfile } from './types'

/**
 * Mapiranje jedne kategorije potreba -> parcijalna a11y podešavanja.
 * Više kategorija se spaja preko `mergeDerived` (vidi pravila niže).
 */
const PER_CATEGORY: Record<NeedsCategory, Partial<AccessibilitySettings>> = {
  dyslexia: {
    fontFamily: 'dyslexic',
    letterSpacing: 'wide',
    wordSpacing: 'wide',
    lineHeight: 'wide',
    ttsKaraoke: true,
    easyReadDefault: true,
  },
  'low-vision': {
    fontSize: 'xl',
    contrastMode: 'high',
    enhancedFocus: true,
  },
  blindness: {
    enhancedFocus: true,
    reducedMotion: true,
  },
  'color-blindness': {
    // colorBlindFilter se postavlja iz profile.colorBlindType (vidi deriveSettings)
  },
  autism: {
    reducedMotion: true,
    autoplayVideo: false,
    distractionFree: true,
  },
  adhd: {
    reducedMotion: true,
    autoplayVideo: false,
    distractionFree: true,
  },
  intellectual: {
    fontSize: 'lg',
    easyReadDefault: true,
    ttsKaraoke: true,
  },
  'deaf-hoh': {
    captionsDefault: true,
    signLanguagePreferred: true,
  },
  motor: {
    enhancedFocus: true,
  },
}

const FONT_SIZE_RANK: Record<FontSize, number> = {
  sm: 0,
  md: 1,
  lg: 2,
  xl: 3,
  xxl: 4,
}

function largerFontSize(a: FontSize | undefined, b: FontSize): FontSize {
  if (a === undefined) return b
  return FONT_SIZE_RANK[a] >= FONT_SIZE_RANK[b] ? a : b
}

/**
 * Spaja dve parcijalne postavke uz pravila razrešenja konflikta:
 * - fontSize: uzmi veći (pristupačnije)
 * - fontFamily: dyslexic ima prioritet nad lexend/system
 * - spacing/lineHeight: 'wide' ima prioritet nad 'normal'
 * - contrastMode: 'high' ima prioritet nad 'normal'
 * - boolean: OR (jednom uključeno ostaje uključeno)
 * - ostalo (colorBlindFilter, tts*): kasniji izvor pobeđuje ako je definisan
 */
function mergeDerived(
  acc: Partial<AccessibilitySettings>,
  next: Partial<AccessibilitySettings>,
): Partial<AccessibilitySettings> {
  const result: Partial<AccessibilitySettings> = { ...acc }

  if (next.fontSize !== undefined) {
    result.fontSize = largerFontSize(result.fontSize, next.fontSize)
  }
  if (next.fontFamily !== undefined) {
    result.fontFamily =
      result.fontFamily === 'dyslexic' || next.fontFamily === 'dyslexic'
        ? 'dyslexic'
        : next.fontFamily
  }
  if (next.letterSpacing !== undefined) {
    result.letterSpacing = result.letterSpacing === 'wide' ? 'wide' : next.letterSpacing
  }
  if (next.wordSpacing !== undefined) {
    result.wordSpacing = result.wordSpacing === 'wide' ? 'wide' : next.wordSpacing
  }
  if (next.lineHeight !== undefined) {
    result.lineHeight = result.lineHeight === 'wide' ? 'wide' : next.lineHeight
  }
  if (next.contrastMode !== undefined) {
    result.contrastMode = result.contrastMode === 'high' ? 'high' : next.contrastMode
  }

  const booleanKeys = [
    'reducedMotion',
    'autoplayVideo',
    'enhancedFocus',
    'ttsKaraoke',
    'easyReadDefault',
    'captionsDefault',
    'signLanguagePreferred',
    'distractionFree',
  ] as const
  for (const key of booleanKeys) {
    if (next[key] !== undefined) {
      result[key] = Boolean(result[key]) || Boolean(next[key])
    }
  }

  if (next.colorBlindFilter !== undefined) result.colorBlindFilter = next.colorBlindFilter
  if (next.ttsEngine !== undefined) result.ttsEngine = next.ttsEngine
  if (next.ttsRate !== undefined) result.ttsRate = next.ttsRate
  if (next.ttsVoiceName !== undefined) result.ttsVoiceName = next.ttsVoiceName

  return result
}

/**
 * Čista funkcija: profil potreba -> predlog a11y podešavanja (parcijalan).
 * Rezultat se merge-uje na DEFAULT_ACCESSIBILITY_SETTINGS na pozivnoj strani,
 * a korisnik ga može korigovati pre snimanja.
 */
export function deriveSettings(profile: NeedsProfile): Partial<AccessibilitySettings> {
  let derived: Partial<AccessibilitySettings> = {}

  for (const category of profile.categories) {
    derived = mergeDerived(derived, PER_CATEGORY[category])
  }

  // Daltonizam: primeni izabrani tip filtera (autizam/none ostaje 'none' default-om)
  if (profile.categories.includes('color-blindness') && profile.colorBlindType) {
    derived = { ...derived, colorBlindFilter: profile.colorBlindType }
  }

  return derived
}
